import React, { useEffect, useState } from 'react';
import '../BuyerSelectionPopup.scss';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../../../slices/marketplace/timeline/models/engagement-data.model';
import { EngagementProcess } from '../../../../slices/marketplace/timeline/models/engagement-process.model';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { MarketplaceOpportunityStatus } from '../../../../slices/marketplace/opportunity/models/opportunity-status';
import { useStateSelector } from '../../../../store/selectors';
import { LevelOfService, isSfas } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import Helpers from '../../../../utils/helper';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { NdaTermSelectionType } from '../../../../slices/marketplace/timeline/models/enums/nda-term-selection-type';
import { DataNdaTermsType } from '../../../../slices/marketplace/timeline/models/enums/data-nda-term-type';
import { Row } from 'reactstrap';
import { DocumentDetailsContentType } from '../../../../slices/marketplace/timeline/models/enums/document-details-content-type';
import {
    DocumentStatusExecutionStatus,
    DocumentStatusExecutionStatusOptions,
} from '../../../../slices/marketplace/timeline/models/enums/document-execution-status.enum';
import { EngagementProcessStatus } from '../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { PricesCalculationHelper } from '../../../../utils/pricesCalculationHelper';
import PortalTooltip from '../../../../components/PortalTooltip/PortalTooltip';
import { dfmOutcomeBeforeTheBreakOf } from '../../../../slices/marketplace/timeline/utils/dfm-outcome-before-the-break';
import { buyersAlreadySent, SelectionRound } from '../../../../slices/marketplace/timeline/utils/catch-up-wait';
import { useBuyerSelectionPaidBefore } from '../../../../custom-hooks/useBuyerSelectionPaidBefore';

interface FinancialModelSelectionFormProps {
    isAdminView: boolean;
    isReadOnly?: boolean;
    events: TimelineEvent[];
    targetEvent: TimelineEvent;
    engagementData: EngagementData;
    engagementProcess: EngagementProcess;
    levelOfService: LevelOfService;
    engagementId: number;
    setSelectedBuyerId(value: number): void;
    setIsBuyerProfilePopupVisible(value: boolean): void;
    isLoading: boolean;
    onPayAndSendClick(
        selectedBuyerIds: number[],
        pricePerBuyer: number,
        totalPrice: number,
        firstNAsOneCount: number,
        eventId: string,
        creditablePortion: number,
        chargedBuyersCount: number
    ): void;
    onCancelClick(): void;
    onFinalizeSelectionClick(): void;
    setTypeOfSelectionForConfirmation(): void;
    eventId: string;
    actionId: number;
    sellersOwnRound?: SelectionRound;
}

export const FinancialModelSelectionForm = (
    props: FinancialModelSelectionFormProps
) => {
    const [selectedBuyerIds, setSelectedBuyerIds] = useState<number[]>([]);
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const [alreadySelectedBuyerIds, setAlreadySelectedBuyerIds] = useState<number[]>([]);
    const paidBeforeBuyerIds = useBuyerSelectionPaidBefore(
        props.engagementId,
        props.eventId,
        !isSfas(props.levelOfService)
    );
    const [chargedBuyersCount, setChargedBuyersCount] = useState(0);
    const [pricePerBuyer, setPricePerBuyer] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [firstNAsOneCount, setFirstNAsOneCount] = useState(0);
    const [creditablePortion, setCreditablePortion] = useState(0);

    useEffect(() => {
        setAlreadySelectedBuyerIds(
            buyersAlreadySent(
                [{ process: props.engagementProcess, actionId: props.actionId }, props.sellersOwnRound],
                [MarketplaceOpportunityStatus.Engaged]
            )
        );
    }, []);

    const getValRangeFormat = (minValue: number, maxValue: number) => {
        const minValueFormatted = Helpers.GetCurrencyFormat(minValue, 0, false);
        const maxValueFormatted = Helpers.GetCurrencyFormat(maxValue, 0, false);
        return `${minValueFormatted} - ${maxValueFormatted}`;
    };

    const getLatestBuyerValueRange = (currentBuyer: EngagementBuyer) => {
        const eventsWithValueRange = props.events.filter(
            (x) =>
                x?.linkedAction?.actionType ===
                    TimelineActionType.BuyerExpressionOfInterestWithRange &&
                x.status !== TimelineEventStatus.Upcoming
        );
        const actionIds = eventsWithValueRange.map((x) => x.linkedAction.id);

        const buyerStates = currentBuyer.actionState.filter((x) =>
            actionIds.includes(x.actionId)
        );

        const notEmptyStates = buyerStates.filter(
            (x) => x.maxPrice !== null && x.minPrice !== null
        );
        if (notEmptyStates.length === 0) {
            return null;
        }
        const latestNotEmptyBuyerState = notEmptyStates.sort(
            (a, b) =>
                new Date(b.dateCreated).getTime() -
                new Date(a.dateCreated).getTime()
        )[0];
        return latestNotEmptyBuyerState;
    };

    const formatYears = (type: DataNdaTermsType) => {
        if (type === DataNdaTermsType.OneYear) return '1 Year';
        if (type === DataNdaTermsType.NA) return 'N/A';

        const string = type.toString();

        return `${string} Years`;
    };

    const getNdaTerms = (currentBuyer: EngagementBuyer) => {
        const ndaTermSelection = props.engagementData.ndaTermSelectionList.find(
            (x) => x.type === NdaTermSelectionType.DataNdaTerms
        );

        const termsNotProvided = 'NDA Terms Not Selected';
        if (!ndaTermSelection) {
            return termsNotProvided;
        }
        const ndaTerms = ndaTermSelection.ndaTermsList;
        if (ndaTerms.length === 0) {
            return termsNotProvided;
        }
        const buyerTerms = ndaTerms.find(
            (x) =>
                x.buyerId === currentBuyer.buyerId &&
                !!x.agreedTermsToDocumentList?.length
        );

        if (!buyerTerms) {
            return termsNotProvided;
        }
        const sellerTerms = ndaTerms.find(
            (x) =>
                x.id ==
                buyerTerms.agreedTermsToDocumentList?.find((f) => f)
                    ?.agreedSellerNdaTermsId
        );

        if (!sellerTerms) {
            return termsNotProvided;
        }
        return (
            <>
                <Row>
                    <label className="mt-1">
                        Non-Disclosure Period:{' '}
                        <span className="period-value">
                            {sellerTerms.nonDisclosurePeriod != null
                                ? formatYears(sellerTerms.nonDisclosurePeriod)
                                : 'NA'}
                        </span>
                    </label>
                </Row>
                <Row>
                    <div className="non-solicitation-period">
                        <label>
                            DVMs / Management,{' '}
                            {buyerTerms.nonSolicitationDvmNamesVisible !=
                                null &&
                            sellerTerms.nonSolicitationDvmNamesVisible !=
                                null ? (
                                <span>
                                    Names Visible:{' '}
                                    <span className="period-value">
                                        {formatYears(
                                            sellerTerms.nonSolicitationDvmNamesVisible
                                        )}
                                    </span>
                                </span>
                            ) : buyerTerms.nonSolicitationDvmNamesVisible !=
                                  null &&
                              sellerTerms.nonSolicitationDvmNamesVisible !=
                                  null ? (
                                <span>
                                    Names Blinded:{' '}
                                    <span className="period-value">
                                        {formatYears(
                                            sellerTerms.nonSolicitationDvmNamesBlinded
                                        )}
                                    </span>
                                </span>
                            ) : (
                                'NA'
                            )}
                        </label>
                        <label>
                            Staff,{' '}
                            {buyerTerms.nonSolicitationStaffNamesVisible !=
                                null &&
                            sellerTerms.nonSolicitationStaffNamesVisible !=
                                null ? (
                                <span>
                                    Names Visible:{' '}
                                    <span className="period-value">
                                        {formatYears(
                                            sellerTerms.nonSolicitationStaffNamesVisible
                                        )}
                                    </span>
                                </span>
                            ) : buyerTerms.nonSolicitationStaffNamesBlinded !=
                                  null &&
                              sellerTerms.nonSolicitationStaffNamesBlinded !=
                                  null ? (
                                <span>
                                    Names Blinded:{' '}
                                    <span className="period-value">
                                        {formatYears(
                                            sellerTerms.nonSolicitationStaffNamesBlinded
                                        )}
                                    </span>
                                </span>
                            ) : (
                                'NA'
                            )}
                        </label>
                    </div>
                </Row>
            </>
        );
    };

    const columns = [
        {
            dataField: 'companyName',
            text: 'Buyer',
            sort: false,
            formatter: (cellContent: string, currentBuyer: EngagementBuyer) => {
                const outcomeBeforeTheBreak = dfmOutcomeBeforeTheBreakOf(currentBuyer);
                const outcomeAnchorId = `dfm-outcome-before-the-break-${currentBuyer.buyerId}`;
                return (
                    <div
                        className="link-text"
                        id={outcomeAnchorId}
                        onClick={() => {
                            props.setSelectedBuyerId(currentBuyer.buyerId);
                            props.setIsBuyerProfilePopupVisible(true);
                        }}>
                        {cellContent}
                        {outcomeBeforeTheBreak && (
                            <PortalTooltip
                                anchorId={outcomeAnchorId}
                                delayShow={0}
                                place="top"
                                content={outcomeBeforeTheBreak}
                            />
                        )}
                    </div>
                );
            },
        },
        {
            dataField: 'id',
            text: 'Data NDA Terms',
            sort: false,
            headerClasses: 'data-nda-terms-header',
            formatter: (_: number, currentBuyer: EngagementBuyer) => {
                return getNdaTerms(currentBuyer);
            },
        },
        {
            dataField: 'id',
            text: 'Initial Valuation Range Indication',
            headerClasses: 'initial-valuation-range-header',
            sort: false,
            formatter: (_: number, currentBuyer: EngagementBuyer) => {
                const latestNotEmptyBuyerState =
                    getLatestBuyerValueRange(currentBuyer);
                if (latestNotEmptyBuyerState == null) {
                    return 'Valuation Range Was Not Provided';
                }

                return getValRangeFormat(
                    latestNotEmptyBuyerState.minPrice,
                    latestNotEmptyBuyerState.maxPrice
                );
            },
        },
        {
            dataField: 'id',
            text: 'Status',
            headerClasses: 'status-header',
            sort: false,
            formatter: (_: number, currentBuyer: EngagementBuyer) => {
                const actionId = props.actionId;
                const events = props.events;
                const documents = props.engagementData.documentsList;
                const dfmDocumnet = documents.filter(
                    (doc) =>
                        doc.contentType ===
                        DocumentDetailsContentType.FinancialModel
                );
                const dfmEvents = events.filter((x) =>
                    dfmDocumnet.some(
                        (y) => y.id === x.linkedAction?.referenceItemId
                    )
                );
                const dfmActions = dfmEvents
                    .filter((x) => x.status === TimelineEventStatus.Completed)
                    .map((x) => x.linkedAction?.id);
                const buyerActionStates = currentBuyer.actionState.filter((x) =>
                    dfmActions.some((y) => y === x.actionId)
                );

                const buyerActionState = currentBuyer.actionState?.find(
                    (x) => x.actionId === actionId
                );

                if (!!!buyerActionState) {
                    return 'Not Offered';
                }

                if (
                    buyerActionStates.some(
                        (x) =>
                            x.buyerStatus ===
                            MarketplaceOpportunityStatus.Engaged
                    )
                ) {
                    return DocumentStatusExecutionStatusOptions[
                        DocumentStatusExecutionStatus.Accepted
                    ];
                }

                return DocumentStatusExecutionStatusOptions[
                    DocumentStatusExecutionStatus.Offered
                ];
            },
        },
        ...(!props.isAdminView
            ? [
                  {
                      dataField: 'buyerId',
                      text: 'Send',
                      sort: false,
                      formatter: (
                          cellContent: number,
                          currentBuyer: EngagementBuyer
                      ) => {
                          const buyerStates = currentBuyer.actionState;
                          const currentActionState = buyerStates.find(
                              (x) => x.actionId === props.actionId
                          );
                          const isBuyerSelected =
                              currentActionState != null &&
                              currentActionState.buyerStatus ===
                                  MarketplaceOpportunityStatus.Engaged;
                          return isBuyerSelected === true ? (
                              <></>
                          ) : (
                              <input
                                  type="checkbox"
                                  checked={selectedBuyerIds.includes(
                                      cellContent
                                  )}
                                  disabled={
                                      props.isReadOnly ||
                                      props.targetEvent?.status ===
                                          TimelineEventStatus.Completed ||
                                      props.engagementProcess?.status ===
                                          EngagementProcessStatus.Completed
                                  }
                                  onChange={(event) => {
                                      const isChecked = event.target.checked;
                                      if (isChecked === true) {
                                          setSelectedBuyerIds([
                                              ...selectedBuyerIds,
                                              cellContent,
                                          ]);
                                      } else {
                                          setSelectedBuyerIds(
                                              selectedBuyerIds.filter(
                                                  (id) => id !== cellContent
                                              )
                                          );
                                      }
                                  }}
                              />
                          );
                      },
                  },
              ]
            : []),
    ];

    useEffect(() => {
        calculateTotalPrice();
    }, [selectedBuyerIds, alreadySelectedBuyerIds, paidBeforeBuyerIds]);

    const calculateTotalPrice = () => {
        const levelOfService = props.levelOfService;

        const brokreagePricing =
            PricesCalculationHelper.getBrokeragePricingByLevelOfService(
                levelOfService,
                marketplacePricing
            );

        const scheduleRow = PricesCalculationHelper.getScheduleRow(brokreagePricing.transactionFeeSchedule, props.engagementProcess.startLtmRevenue);
        const pricePerBuyer = scheduleRow?.financialModelFeePerBuyer ?? 0;
        const firstXAsOne =
            brokreagePricing.transactionFees.financialModelFirstXCount;
        const creditablePortion =
            brokreagePricing.transactionFees.financialModelFeeCreditable;

        // [#1309] Buyers paid for on this step before are selected again at no charge.
        const { chargedBuyersCount, totalPrice: orderTotalPrice } =
            PricesCalculationHelper.priceBuyerSelection(
                selectedBuyerIds,
                alreadySelectedBuyerIds,
                paidBeforeBuyerIds,
                firstXAsOne,
                pricePerBuyer
            );

        const creditableValue = (creditablePortion * orderTotalPrice) / 100;
        setPricePerBuyer(pricePerBuyer);
        setChargedBuyersCount(chargedBuyersCount);
        setFirstNAsOneCount(firstXAsOne);
        setTotalPrice(orderTotalPrice);
        setCreditablePortion(creditableValue);
    };

    return (
        <div className="fin-model-selection-form">
            <BootstrapTable
                striped
                key={selectedBuyerIds.length}
                bordered
                bootstrap4
                keyField="Id"
                data={
                    props.engagementProcess?.buyers?.filter(
                        (x) => x.isActive
                    ) ?? []
                }
                columns={columns}
                noDataIndication="No Data available"
            />
            {!isSfas(props.levelOfService) && (
                <>
                    <div className="bold">
                        Total Cost:{' '}
                        {Helpers.GetCurrencyFormat(totalPrice, 2, true)}
                    </div>
                    <div>
                        Creditable Portion:{' '}
                        {Helpers.GetCurrencyFormat(creditablePortion, 2, true)}
                    </div>
                </>
            )}
            <div className="buttons-wrapper">
                <button
                    className="btn btn-cancel"
                    disabled={props.isLoading}
                    onClick={() => {
                        props.onCancelClick();
                    }}>
                    Cancel
                </button>
                {!props.isAdminView && (
                    <>
                        <ButtonLoader
                            buttonText={
                                !isSfas(props.levelOfService)
                                    ? 'Pay & Send'
                                    : 'Send'
                            }
                            loaderButtonText={''}
                            disabled={
                                props.isReadOnly ||
                                props.isLoading ||
                                selectedBuyerIds.length === 0
                            }
                            isLoading={props.isLoading}
                            onClick={() => {
                                props.onPayAndSendClick(
                                    selectedBuyerIds,
                                    pricePerBuyer,
                                    totalPrice,
                                    firstNAsOneCount,
                                    props.eventId,
                                    creditablePortion,
                                    chargedBuyersCount
                                );
                            }}
                            className={'btn btn-primary'}
                        />
                        <button
                            className="btn btn-primary"
                            disabled={
                                props.isReadOnly ||
                                props.isLoading ||
                                props.targetEvent?.status ===
                                    TimelineEventStatus.Completed
                            }
                            onClick={() => {
                                props.setTypeOfSelectionForConfirmation();
                                props.onFinalizeSelectionClick();
                            }}>
                            Finalize selection
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
